/**
 * Pieces shared by the receiving screens (batch 8 T12c2, #3033): the owner/target boundary, error and pending-request notices.
 */

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useReceiptCacheGuard } from '@/hooks/usePurchaseReceipts';
import { describeReceivingError } from './receivingErrors';
import type { JournalEntry, JournalStatus } from './receivingJournal';
import { accessKey, useReceivingAccess } from './receivingSession';
import type { CloseShortBody, ReceiveGoodsBody, ResolveHoldBody } from './receivingTypes';

export interface DialogBoundary {
  readonly open: boolean;
  readonly onClose: () => void;
}

interface BoundaryProps {
  /** The purchase order the subtree works on. A different one remounts it. */
  target: string | null;
  /**
   * A dialog: it renders only while open, and a dialog that was opened by one owner (or for one target) and is still open when the
   * owner, the generation, the rights or the target change is closed instead of being shown to whoever is there now.
   */
  dialog?: DialogBoundary;
  children: ReactNode;
}

/**
 * Remounts its subtree whenever the owner of the screen, what the owner may do or the target changes (batch 8 T12c2, #3033).
 *
 * Drafts, a reviewed summary, names, costs and a request's state belong to the signed-in owner (tenant, user, session family,
 * generation), to the rights it holds and to the purchase order. The key is read in the very render that sees the change, so no
 * frame shows another owner's drafts, costs or names, and nothing of the old subtree survives to report a late result. A locked or
 * signed-out session renders nothing at all. Unresolved keyed requests are not state of the subtree: they live in the owner's journal.
 */
export function ReceivingBoundary({ target, dialog, children }: BoundaryProps) {
  const access = useReceivingAccess();
  useReceiptCacheGuard();
  const key = `${accessKey(access)}|${target ?? ''}`;
  const openedUnder = useRef<string | null>(null);
  const open = dialog?.open ?? true;
  if (!open) openedUnder.current = null;
  else if (openedUnder.current === null) openedUnder.current = key;
  const stale = dialog !== undefined && open && openedUnder.current !== key;
  const onClose = dialog?.onClose;
  useEffect(() => {
    if (stale) onClose?.();
  }, [stale, onClose]);
  if (!access.active || stale || !open) return null;
  return <Fragment key={key}>{children}</Fragment>;
}

/** A refusal or failure, in words the user can act on. */
export function ReceivingErrorAlert({ error }: { error: unknown }) {
  const { t } = useTranslation('procurementReceiving');
  if (error === null || error === undefined) return null;
  return (
    <Alert severity="error" role="alert">
      {describeReceivingError(error, t)}
    </Alert>
  );
}

/** What an unresolved request is, in a sentence. */
export function describeEntry(entry: JournalEntry, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (entry.kind === 'receive') {
    const body = entry.request.body as ReceiveGoodsBody;
    return t('pending.what.receive', { count: body.lines.length });
  }
  if (entry.kind === 'resolve_hold') {
    const body = entry.request.body as ResolveHoldBody;
    return t(body.decision === 'accept' ? 'pending.what.holdAccept' : 'pending.what.holdReject', { count: body.quantity });
  }
  const body = entry.request.body as CloseShortBody;
  return t('pending.what.closeShort', { reason: body.reason });
}

interface PendingProps {
  entry: JournalEntry;
  /** The last answer of this screen's own attempt; `null` when the request was found again after a reload. */
  error: unknown;
  busy: boolean;
  onRetry: () => void;
  onDiscard: () => void;
  journalStatus: JournalStatus;
}

/**
 * The write may or may not have happened. The same request (target, payload and key) is held; sending it again leaves the server
 * to replay its first answer or to refuse a second run. Giving it up is the user's explicit, confirmed choice, because a fresh
 * request could receive the same goods twice.
 */
export function PendingOperationAlert({ entry, error, busy, onRetry, onDiscard, journalStatus }: PendingProps) {
  const { t } = useTranslation('procurementReceiving');
  const [confirming, setConfirming] = useState(false);
  const what = describeEntry(entry, t as never);
  return (
    <Alert severity="warning" role="alert">
      <AlertTitle>{t('pending.title')}</AlertTitle>
      <Stack spacing={1}>
        <span>{t('pending.body', { what })}</span>
        <span>{error === null || error === undefined ? t('pending.afterReload') : describeReceivingError(error, t)}</span>
        {entry.attempts > 1 && <span>{t('pending.attempts', { count: entry.attempts })}</span>}
        {journalStatus === 'unavailable' && <span>{t('journal.unavailableHolding')}</span>}
        {entry.durability === 'lost' && <span>{t('pending.lost')}</span>}
        {entry.durability === 'replaced' && <span>{t('pending.replaced')}</span>}
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" color="warning" onClick={onRetry} disabled={busy}>
            {t('pending.retry')}
          </Button>
          <Button size="small" onClick={() => setConfirming(true)} disabled={busy}>
            {t('pending.discard')}
          </Button>
        </Stack>
      </Stack>
      <ConfirmDialog
        open={confirming}
        title={t('pending.discardTitle')}
        message={t('pending.discardWarning', { what })}
        confirmText={t('pending.discardConfirm')}
        confirmColor="warning"
        onConfirm={() => {
          setConfirming(false);
          onDiscard();
        }}
        onCancel={() => setConfirming(false)}
      />
    </Alert>
  );
}

interface JournalNoticeProps {
  status: JournalStatus;
  onDiscardDamaged: () => void;
  /** Probes the browser's storage again after it refused; true when it keeps a record again. Sends nothing. */
  onRecheck?: () => boolean;
}

/**
 * The browser's record of earlier requests could not be kept or read. Unreadable: nothing new starts until the user knowingly
 * discards it (an earlier request cannot be ruled out). Unavailable: nothing is sent, because a request that cannot be kept
 * cannot be repeated safely.
 */
export function JournalNotice({ status, onDiscardDamaged, onRecheck }: JournalNoticeProps) {
  const { t } = useTranslation('procurementReceiving');
  const [confirming, setConfirming] = useState(false);
  const [stillFailing, setStillFailing] = useState(false);
  if (status === 'corrupt') {
    return (
      <Alert severity="error" role="alert">
        <AlertTitle>{t('journal.corruptTitle')}</AlertTitle>
        <Stack spacing={1}>
          <span>{t('journal.corruptBody')}</span>
          <Stack direction="row">
            <Button size="small" color="error" onClick={() => setConfirming(true)}>
              {t('journal.corruptDiscard')}
            </Button>
          </Stack>
        </Stack>
        <ConfirmDialog
          open={confirming}
          title={t('journal.corruptDiscardTitle')}
          message={t('journal.corruptDiscardWarning')}
          confirmText={t('journal.corruptDiscardConfirm')}
          confirmColor="error"
          onConfirm={() => {
            setConfirming(false);
            onDiscardDamaged();
          }}
          onCancel={() => setConfirming(false)}
        />
      </Alert>
    );
  }
  if (status === 'unavailable') {
    return (
      <Alert severity="error" role="alert">
        <AlertTitle>{t('journal.unavailableTitle')}</AlertTitle>
        <Stack spacing={1}>
          <span>{t('journal.unavailable')}</span>
          {stillFailing && <span>{t('journal.recheckFailed')}</span>}
          {onRecheck && (
            <Stack direction="row">
              <Button size="small" color="error" onClick={() => setStillFailing(!onRecheck())}>
                {t('journal.recheck')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Alert>
    );
  }
  return null;
}

/** Outcome of a send for a screen that only needs to say "not sent, and why" in addition to the usual statuses. */
export function blockedMessage(reason: 'pending' | 'corrupt' | 'storage' | 'full' | 'missing' | 'changed', t: (key: string) => string): string {
  return t(`blocked.${reason}`);
}
